import {
  Card,
  CardBody,
  Row,
  Col,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
  Breadcrumb,
  BreadcrumbItem,
} from "reactstrap";
import ReactTable from "components/ReactTable/ReactTable.js";
import { useMemo, useState, React, useEffect, useContext } from "react";
import { NavLink, useHistory, useLocation } from "react-router-dom";
import { MainContext } from "context/MainContext.js";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import { faHome } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Utilities from "components/Utils/utilities";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import LoadingOverlay from "react-loading-overlay";
import { ToastContainer, toast } from "react-toastify";
import ReactBSAlert from "react-bootstrap-sweetalert";

const PortList = () => {
  const history = useHistory();
  const location = useLocation();
  const privileges = useContext(MainContext);
  const [forceRefreshCount, setForceRefreshCount] = useState(0);
  const [showLoading, setShowLoading] = useState(false);

  useEffect(() => {
    if (!Utilities.hasAccess(privileges["devices.ports.display"])) {
      history.push({
        pathname: "/",
      });
    } else if (Utilities.hasAgreedTerms() == "false") {
      history.push({
        pathname: "/auth/terms",
      });
    }
  }, []);

  const [params, setParams] = useState(location.state?.value ?? "");

  const redirectToEndpointList = (portKey) => {
    const location = {
      pathname: "/admin/endpoints/list",
      state: {
        value:
          "serialNumber||" + params.split("||")[1] + "||portKey||" + portKey,
      },
    };
    history.push(location);
  };

  const enablePort = (portId, enable) => {
    setShowLoading(true);
    fetch("/api/v1/port/edit", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        sessionId: sessionStorage.getItem("sessionId"),
      },
      body: JSON.stringify({
        portId: portId,
        state: enable,
      }),
    })
      .then((response) => response.json())
      .then((actualData) => {
        setShowLoading(false);

        if (actualData.error_code || actualData.status != 200) {
          const action = enable ? "disabling" : "enabling";
          toast.error(`A problem occurred while ${action} the port`, {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: true,
            closeOnClick: false,
            pauseOnHover: false,
            draggable: false,
            theme: "colored",
          });
        } else {
          const action = enable ? "disabled" : "enabled";
          toast.success(`Port ${action} successfully`, {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: true,
            closeOnClick: false,
            pauseOnHover: false,
            draggable: false,
            theme: "colored",
          });
          setForceRefreshCount((prevValue) => prevValue + 1);
        }
      })
      .catch((err) => {
        setShowLoading(false);
        console.log(err.message);
      });
  };

  const columns = useMemo(
    () => [
      {
        Header: "Status",
        accessor: "shutdown",
        sortBy: "shutdown",
        Cell: (props) => {
          const { value, row } = props;

          const icon = value ? (
            <FaTimesCircle
              className="tableIconFontSize"
              style={{ color: "grey" }}
            />
          ) : (
            <FaCheckCircle className="text-primary tableIconFontSize" />
          );

          const tooltipId = `vmElement-${row.index}`;

          return (
            <>
              <a id={tooltipId} className="vmElement">
                {icon}
              </a>
              <ReactTooltip
                anchorSelect={`#${tooltipId}`}
                place="top"
                style={{ textAlign: "left", zIndex: "100" }}
              >
                <div>{value ? "Disabled" : "Active"}</div>
              </ReactTooltip>
            </>
          );
        },
        flex: "0 1 5%",
      },
      {
        Header: "Link Up",
        accessor: "link",
        sortBy: "link",
        Cell: (props) => {
          return props.value ? (
            <FaCheckCircle className="text-primary tableIconFontSize" />
          ) : (
            ""
          );
        },
        flex: "0 1 5%",
      },
      {
        Header: "Interface",
        accessor: "key",
        sortBy: "position",
        flex: "1 1 0",
        Cell: (props) => {
          return props.value == "Gi 1/1" ? "MGMT" : props.value;
        },
      },
      {
        Header: "Description",
        accessor: "description",
        sortBy: "description",
        flex: "1 1 0",
      },
      {
        Header: "Fdx",
        accessor: "fdx",
        sortBy: "fdx",
        Cell: (props) => {
          return props.value ? (
            <FaCheckCircle className="text-primary tableIconFontSize" />
          ) : (
            ""
          );
        },
        show: false,
        flex: "1 1 0",
      },
      {
        Header: "Fiber",
        accessor: "fiber",
        sortBy: "fiber",
        Cell: (props) => {
          return props.value ? (
            <FaCheckCircle className="text-primary tableIconFontSize" />
          ) : (
            ""
          );
        },
        show: false,
        flex: "1 1 0",
      },
      {
        Header: "Tx Fault",
        accessor: "txFault",
        sortBy: "txFault",
        Cell: (props) => {
          return props.value ? (
            <FaCheckCircle className="text-primary tableIconFontSize" />
          ) : (
            ""
          );
        },
        show: false,
        flex: "1 1 0",
      },
      {
        Header: "Loss Of Signal",
        accessor: "lossOfSignal",
        sortBy: "lossOfSignal",
        Cell: (props) => {
          return props.value ? (
            <FaCheckCircle className="text-primary tableIconFontSize" />
          ) : (
            ""
          );
        },
        show: false,
        flex: "1 1 0",
      },
      {
        Header: "Present",
        accessor: "present",
        sortBy: "present",
        Cell: (props) => {
          return props.value ? (
            <FaCheckCircle className="text-primary tableIconFontSize" />
          ) : (
            ""
          );
        },
        show: false,
        flex: "1 1 0",
      },
      {
        Header: "SFP Type",
        accessor: "sfpType",
        sortBy: "sfpType",
        flex: "1 1 0",
      },
      {
        Header: "SFP Vendor",
        accessor: "sfpVendorName",
        sortBy: "sfpVendorName",
        show: false,
        flex: "1 1 0",
      },
      {
        Header: "SFP Vendor PN",
        accessor: "sfpVendorPn",
        sortBy: "sfpVendorPn",
        show: false,
        flex: "1 1 0",
      },
      {
        Header: "SFP Vendor Rev",
        accessor: "sfpVendorRev",
        sortBy: "sfpVendorRev",
        show: false,
        flex: "1 1 0",
      },
      {
        Header: "SFP Vendor SN",
        accessor: "sfpVendorSn",
        sortBy: "sfpVendorSn",
        show: false,
        flex: "1 1 0",
      },
      {
        Header: "Endpoints",
        accessor: "endpointCount",
        sortBy: "endpointCount",
        flex: "1 1 0",
        Cell: (props) => {
          // Set the right background color base on the delay or the status
          var color = "";
          if (
            props.value >
            props.settings.maximum_number_coax_endpoints_tca_error_level
          ) {
            color = "rgba(232, 80, 77, 1)"; // Muted Red
          } else if (
            props.value >
            props.settings.maximum_number_coax_endpoints_tca_warning_level
          ) {
            color = "rgba(240, 205, 102, 1)"; // Soft Yellow
          }

          return (
            <div style={{ backgroundColor: color, width: "100%" }}>
              <a
                onClick={() => redirectToEndpointList(props.row.original.key)}
                style={{
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                {props.value}
              </a>
            </div>
          );
        },
      },
      {
        Header: "",
        accessor: "actions",
        Cell: (props) => {
          return (
            <UncontrolledDropdown direction="down">
              <DropdownToggle
                caret
                color="info"
                style={{
                  padding: "6px 14px 6px 14px",
                  margin: "0px",
                }}
              >
                Actions
              </DropdownToggle>
              <DropdownMenu container={"body"}>
                {Utilities.hasAccess(privileges["devices.ports.edit"]) ? (
                  <>
                    {props.row.original.shutdown ? (
                      <DropdownItem
                        onClick={() => enablePort(props.row.original.id, false)}
                      >
                        Enable Port
                      </DropdownItem>
                    ) : (
                      <DropdownItem
                        onClick={() => enablePort(props.row.original.id, true)}
                      >
                        Disable Port
                      </DropdownItem>
                    )}
                  </>
                ) : (
                  ""
                )}
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
        flex: "1 1 0",
        padding: "0px",
        sortable: false,
        filterable: false,
      },
    ],
    [],
  );

  return (
    <>
      <ToastContainer />

      <div className="content">
        <LoadingOverlay
          active={showLoading}
          spinner
          text="Loading"
          className={showLoading ? "loading" : ""}
        />
        <Row>
          <Col md="12">
            <Card>
              <CardBody>
                <Breadcrumb>
                  <BreadcrumbItem>
                    <NavLink
                      to={{
                        pathname: "/admin/dashboard",
                      }}
                    >
                      <FontAwesomeIcon icon={faHome} />
                    </NavLink>
                  </BreadcrumbItem>
                  {params.includes("serialNumber") ? (
                    <BreadcrumbItem>
                      <NavLink
                        to={{
                          pathname: "/admin/devices/list",
                        }}
                      >
                        GAM
                      </NavLink>
                      <i>&lt;{params.split("||")[1]}&gt;</i>
                    </BreadcrumbItem>
                  ) : (
                    ""
                  )}
                  <BreadcrumbItem>Ports</BreadcrumbItem>
                </Breadcrumb>
              </CardBody>
            </Card>
            <Card>
              <CardBody>
                <ReactTable
                  query="/api/v1/port/list"
                  columns={columns}
                  param={params}
                  title="Ports"
                  className="-striped -highlight primary-pagination"
                  forceRefresh={forceRefreshCount}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default PortList;
